import { useState, useEffect, useCallback, useRef } from "react";
import { Col, Row } from "react-bootstrap";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  organizationDocumentsSelector,
  organizationDocumentSelector,
  actionTypeSelector,
  selectedOrganizationSelector,
  authSelector,
} from "../../reducers";
import { getLocaleText, updateGridData } from "../../actions/datagrid";
import { getOrganizationDocument } from "../../actions/organization/organizationdocumentget";
import { getAllOrganizationDocuments } from "../../actions/organization/organizationdocumentsgetall";
import { deleteOrganizationDocument } from "../../actions/organization/organizationdocumentdelete";
import { AddDocument } from "./documentmodal";
import { SelectOrganization } from "../selectorganization";
import { pencilIconString, trashIconString, restoreIconString, IconAddNew } from "../../common/icons";
import { tagFormatter } from "../../common/utils";
import { loadViewState, saveViewState, sideBar, statusBar, gridSettings, restoreViewState } from "../../common/grid";

const viewStateKey = "organizationDocumentsViewState";

export default function OrganizationDocuments() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);
  const organizationDocuments = useSelector(organizationDocumentsSelector);
  const organizationDocument = useSelector(organizationDocumentSelector);

  const actionType = useSelector(actionTypeSelector);

  const [modalShow, setModalShow] = useState(false);

  useEffect(() => {
    if (selectedOrganization !== null) {
      dispatch(getAllOrganizationDocuments(selectedOrganization.businessIdentifier ?? auth.companyBusinessId));
    }
  }, [selectedOrganization]);

  useEffect(() => {
    switch (actionType) {
      case "UPLOAD_DONE":
        setModalShow(false);
        dispatch(getAllOrganizationDocuments(selectedOrganization.businessIdentifier ?? auth.companyBusinessId));
        break;

      case "POST_ORGANIZATION_DOCUMENT_EDIT_SUCCEEDED":
        updateGridData(gridRef, [organizationDocument]);
        break;

      case "GET_ORGANIZATION_DOCUMENT_SUCCEEDED":
        setModalShow(true);
        break;

      case "DELETE_ORGANIZATION_DOCUMENT_SUCCEEDED":
        dispatch(getAllOrganizationDocuments(selectedOrganization.businessIdentifier ?? auth.companyBusinessId));
        break;
    }
  }, [actionType]);


  /*
  const removeOrganizationDocument = id => {
    if (confirm(t("DeleteDocumentConfirm", "Haluatko varmasti poistaa dokumentin?"))) {
      dispatch(deleteOrganizationDocument(id));
    }
  };
*/

  const columnDefs = [
    {
      headerName: t("Name", "Nimi"),
      field: "documentName",
      width: 150,
      tooltipField: "documentName",
    },
    {
      headerName: t("Date", "Päivämäärä"),
      field: "documentDate",
      filter: "agDateColumnFilter",
      width: 150,
      valueGetter: (params) => {
        const date = params.data?.documentDate?.slice(0, 10);
        return date;
      },
    },
    {
      headerName: t("Category", "Kategoria"),
      field: "documentCategory",
      valueGetter: (params) => {
        const cat = params.data?.documentCategory;
        const map = {
          Certificates: t("Certificates", "Todistukset"),
          Induction: t("Induction", "Perehdytys"),
          AgreementOccupationalHealth: t("AgreementOccupationalHealth", "Työterveyssopimus"),
          AgreementInsurance: t("AgreementInsurance", "Vakuutussopimus"),
          Instructions: t("Instructions", "Ohjeet"),
          PersonnelBenefits: t("PersonnelBenefits", "Henkilöstöedut"),
          CBA: t("CBA", "Työehtosopimus"),
          OccupationalSafetyAndHealth: t("OccupationalSafetyAndHealth", "Työsuojelu"),
          OccupationalHealth: t("OccupationalHealth", "Työterveys"),
          TravelGuidelines: t("TravelGuidelines", "Matkustusohjeet"),
          OccupationalSafety: t("OccupationalSafety", "Työturvallisuus"),
          InformationSecurity: t("InformationSecurity", "Tietoturva"),
          FireSafety: t("FireSafety", "Paloturvallisuus"),
          HelpDesk: t("HelpDesk", "Help desk"),
          DressCode: t("DressCode", "Pukuohjeistus"),
          Ergonomy: t("Ergonomy", "Ergonomia"),
          Other: t("OtherInstruction", "Muu ohjeistus"),
        };
        return map[cat] ?? cat;
      },
    },
    {
      headerName: t("Description", "Kuvaus"),
      field: "documentDescription",
    },
    {
      headerName: t("Keywords", "Avainsanat"),
      field: "tags",
      valueGetter: (params) => tagFormatter(params.data?.tags),
      tooltipValueGetter: (params) => tagFormatter(params.data?.tags),
    },
    {
      headerName: t("DocumentLocation", "Missä"),
      field: "documentLocation",
      valueGetter: (params) => {
        const loc = params.data?.documentLocation;
        const map = {
          DOCUMENTS: t("Documents", "Dokumentit"),
          ONBOARDING: "Onboarding",
          OFFBOARDING: "Offboarding",
          USERPORTAL: t("UserPortal", "User portal"),
          OWNPROFILE: t("OwnProfile", "Oma profiili"),
        };
        return map[loc] ?? loc;
      },
    },
    {
      headerName: t("DocumentViewer", "Kenelle"),
      field: "documentViewer",
      valueGetter: (params) => {
        const viewers = params.data?.documentViewers || [];
        const map = {
          HR: t("HR", "HR"),
          SUPERVISOR: t("Supervisor", "Esihenkilö"),
          EMPLOYEE: t("Employee", "Työntekijä"),
        };
        return viewers.map((v) => map[v] ?? v).join(", ");
      },
    },
  ];

  const doubleClickAction = (row) => dispatch(getOrganizationDocument(row.data.documentId));

  const getContextMenuItems = useCallback((params) => {
    let items = [];

    items.push({
      name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
      action: () => restoreViewState(viewStateKey),
      icon: restoreIconString,
    });

    items.push("separator");

    if (!params.api.isPivotMode()) {
      items.push({
        name: t("Edit", "Muokkaa"),
        icon: pencilIconString,
        action: () => dispatch(getOrganizationDocument(params.node?.data.documentId)),
      });

      if (auth.admin) {
        items.push({
          name: t("Delete", "Poista"),
          icon: trashIconString,
          action: () => {
            if (confirm(t("DeleteDocumentConfirm", "Haluatko varmasti poistaa dokumentin?"))) {
              dispatch(deleteOrganizationDocument(params.node?.data.documentId));
            }
          },
        });
      }

      items.push("separator");
    }

    return items;
  }, [dispatch, t, auth]);

  return (
    <>
      <Row className="my-1">
        <Col sm="4">
          <h2 className="viewheading">{t("Documents", "Dokumentit")}</h2>
        </Col>
        <Col sm="4">
          <SelectOrganization />
        </Col>

        {!auth.personalProfile && (
          <Col sm="4" align="right">
            <Row className="g-2">
              <Col />
              <Col md="auto">
                <AddDocument
                  documentData={organizationDocument}
                  organizationId={selectedOrganization?.id}
                  businessIdentifier={selectedOrganization?.businessIdentifier}
                  show={modalShow}
                  setShow={setModalShow}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>

      <Row className="ag-theme-material styles-ag flex-grow-1">
        <AgGridReact
          {...gridSettings}
          ref={gridRef}
          columnDefs={columnDefs}
          sideBar={sideBar}
          statusBar={statusBar}  
          getContextMenuItems={getContextMenuItems}
          getLocaleText={(params) => getLocaleText(t, params)}
          onRowDoubleClicked={doubleClickAction}
          rowData={organizationDocuments}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          initialState={undefined}
          suppressContextMenu={false}
          suppressRowContextMenu={false}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
